import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Icon from './Icon';
import { NAV, SERVICES } from '../data';

const linkCls = ({ isActive }) =>
  `relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors after:absolute after:inset-x-3.5 after:bottom-0.5 after:h-0.5 after:origin-left after:rounded-full after:bg-white after:transition-transform after:duration-300 ${
    isActive ? 'text-white after:scale-x-100' : 'text-white/70 after:scale-x-0 hover:text-white hover:after:scale-x-100'
  }`;

export default function Navbar() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const closeTimer = useRef(null);
  const floating = scrolled || pathname !== '/';

  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 30);
      setHidden(y > last && y > 400);
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // close menus on navigation
  useEffect(() => { setOpen(false); setMega(false); setHidden(false); }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const openMega = () => { clearTimeout(closeTimer.current); setMega(true); };
  const closeMega = () => { closeTimer.current = setTimeout(() => setMega(false), 120); };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${hidden && !open && !mega ? '-translate-y-full' : 'translate-y-0'} ${
        floating || open ? 'bg-ink/90 shadow-[0_8px_30px_-12px_rgba(0,0,0,.6)] backdrop-blur-xl lg:bg-transparent lg:shadow-none lg:backdrop-blur-none' : 'bg-transparent'
      }`}
    >
      <div className={`mx-auto transition-all duration-500 ${floating ? 'lg:mt-3 lg:max-w-6xl lg:rounded-full lg:border lg:border-white/10 lg:bg-ink/85 lg:shadow-[0_12px_40px_-12px_rgba(0,0,0,.7)] lg:backdrop-blur-xl' : 'max-w-7xl'}`}>
      <div className={`flex items-center justify-between px-5 transition-all duration-500 sm:px-8 ${scrolled ? 'h-[60px]' : 'h-[76px]'}`}>
        <Link to="/" aria-label="Origo Private Limited home">
          <img src="/logo.png" alt="Origo Private Limited" className={`w-auto transition-all duration-500 ${scrolled ? 'h-10 sm:h-11' : 'h-12 sm:h-14'}`} />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV.map((l) =>
            l.mega ? (
              <div key={l.to} onMouseEnter={openMega} onMouseLeave={closeMega}>
                <NavLink to={l.to} className={linkCls} onFocus={openMega}>
                  {l.label}
                  <svg viewBox="0 0 24 24" className={`ml-1 inline h-3.5 w-3.5 transition-transform ${mega ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="m6 9 6 6 6-6" /></svg>
                </NavLink>
                <div className={`absolute left-1/2 top-full w-[min(920px,94vw)] -translate-x-1/2 pt-3 transition-all duration-300 ${mega ? 'visible translate-y-0 opacity-100' : 'invisible translate-y-2 opacity-0'}`}>
                  <div className="rounded-3xl border border-white/10 bg-ink-2 p-4 shadow-2xl">
                    <div className="grid grid-cols-3 gap-1">
                      {SERVICES.map((s) => (
                        <Link key={s.slug} to={`/services/${s.slug}`} className="group flex items-center gap-3 rounded-2xl p-3 transition-colors hover:bg-white/5">
                          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand text-white transition-colors group-hover:bg-white group-hover:bg-none group-hover:text-brand"><Icon name={s.icon} className="h-5 w-5" /></span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-white/90 group-hover:text-white">{s.title}</span>
                            <span className="block truncate text-xs text-white/50">{s.tags[0]}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                    <div className="mt-3 flex items-center justify-between rounded-2xl bg-white/5 px-4 py-3">
                      <p className="text-sm text-white/70">Not sure what you need? We’ll scope it with you — free.</p>
                      <Link to="/services" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:underline">View all services <Icon name="arrow" className="h-4 w-4" /></Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <NavLink key={l.to} to={l.to} end={l.to === '/'} className={linkCls}>{l.label}</NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/contact" className="btn-primary hidden !py-2.5 sm:inline-flex">
            Get a Free Quote <Icon name="arrow" className="h-4 w-4" />
          </Link>
          <button
            className="grid h-11 w-11 place-items-center rounded-full border border-white/20 text-white lg:hidden"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            <Icon name={open ? 'close' : 'menu'} />
          </button>
        </div>
      </div>
      </div>

      <div className={`overflow-y-auto transition-[max-height] duration-500 lg:hidden ${open ? 'max-h-[calc(100vh-72px)]' : 'max-h-0'}`}>
        <nav className="container-x flex flex-col gap-1 pb-6" aria-label="Mobile">
          {NAV.map((l) =>
            l.mega ? (
              <div key={l.to}>
                <div className="flex items-center">
                  <NavLink to={l.to} className="flex-1 rounded-xl px-4 py-3 text-lg font-medium text-white/85 hover:text-white">{l.label}</NavLink>
                  <button onClick={() => setMobileServices((v) => !v)} aria-label="Toggle services list" className="grid h-10 w-10 place-items-center text-white/70">
                    <svg viewBox="0 0 24 24" className={`h-5 w-5 transition-transform ${mobileServices ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="m6 9 6 6 6-6" /></svg>
                  </button>
                </div>
                <div className={`grid overflow-hidden pl-4 transition-all duration-300 ${mobileServices ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                  <div className="min-h-0 overflow-hidden">
                    {SERVICES.map((s) => (
                      <Link key={s.slug} to={`/services/${s.slug}`} className="block rounded-lg px-4 py-2 text-[15px] text-white/65 hover:text-white">{s.title}</Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink key={l.to} to={l.to} end={l.to === '/'} className={({ isActive }) => `rounded-xl px-4 py-3 text-lg font-medium hover:bg-white/5 hover:text-white ${isActive ? 'bg-white/10 text-white' : 'text-white/70'}`}>{l.label}</NavLink>
            )
          )}
          <Link to="/contact" className="btn-primary mt-3">Get a Free Quote</Link>
        </nav>
      </div>
    </header>
  );
}
